import React from 'react';
import { usePathname } from 'expo-router';
import { useSession } from '../lib/SessionContext';
import { supabase } from '../lib/supabase';
import { canAccessMainApp } from '../utils/authHelpers';
import {
    supportIsHidden, supportProvider, supportVisibilityEvent,
    type SupportContext, type SupportWidget,
} from '../lib/supportRuntime';

const widgetUrl = process.env.EXPO_PUBLIC_SUPPORT_WIDGET_URL
    || '/support-chat/assets/0.1.0/widget.js';

export default function SupportChat() {
    const pathname = usePathname();
    const { session, isLoading } = useSession();
    const [hidden, setHidden] = React.useState(supportIsHidden);
    const authenticated = canAccessMainApp(session);
    const context: SupportContext = {
        pathname, authenticated, authReady: !isLoading,
        identityKey: authenticated ? session!.user.id : 'anonymous', hidden,
    };
    const latest = React.useRef(context);
    latest.current = context;
    const widget = React.useRef<SupportWidget | null>(null);

    React.useEffect(() => {
        const update = () => setHidden(supportIsHidden());
        window.addEventListener(supportVisibilityEvent, update);
        return () => window.removeEventListener(supportVisibilityEvent, update);
    }, []);

    React.useEffect(() => {
        if (supportProvider !== 'telegram') return;
        let disposed = false;
        const source = new URL(widgetUrl, window.location.origin);
        if (source.origin !== window.location.origin) {
            console.error('Support widget must be served from the website origin.');
            return;
        }
        const start = () => {
            if (disposed || !window.PaperzillaSupport) return;
            widget.current = window.PaperzillaSupport.mount({
                apiBase: '/api/support', context: latest.current,
                getAccessToken: async () => {
                    const { data: { session: current } } = await supabase.auth.getSession();
                    return canAccessMainApp(current) ? current!.access_token : null;
                },
            });
            window.__PZ_SUPPORT_WIDGET__ = widget.current;
        };
        const script = document.createElement('script');
        if (window.PaperzillaSupport) start();
        else {
            script.src = source.href; script.async = true;
            script.onload = start;
            script.onerror = () => console.error('Support widget could not load.');
            document.head.append(script);
        }
        return () => {
            disposed = true; script.remove();
            widget.current?.destroy();
            if (window.__PZ_SUPPORT_WIDGET__ === widget.current) delete window.__PZ_SUPPORT_WIDGET__;
            widget.current = null;
        };
    }, []);

    React.useEffect(() => {
        widget.current?.setContext(latest.current);
    }, [pathname, authenticated, session?.user.id, isLoading, hidden]);
    return null;
}
